/*
  The thing standing between a render throw and a white screen.

  There was none. Measured before this existed: zero `ErrorBoundary`, zero `componentDidCatch` in the
  whole client. A throw in any of forty panels unmounted the entire tree — chart, panels, presence,
  the lot — leaving an empty page with the error only in the console. During a client workshop that
  is not a bug report, it is the end of the session.

  TWO BOUNDARIES, NOT ONE, is the part worth getting right. A single boundary at the root turns every
  panel fault into a whole-app fault, which is the failure it was meant to prevent wearing a nicer
  message. `scope="panel"` contains the damage to one side panel or one modal and leaves the chart,
  the connection and everybody else's presence alive — and a chart the user can still read while one
  pane is broken is a session that continues.

  IT DOES NOT OFFER "TRY AGAIN". A caught render error means this component's props and state are in
  a shape it cannot draw; re-rendering the same thing produces the same throw, and a button that
  looks like a remedy and is not is worse than no button. Reloading is the honest instruction,
  because the store rebuilds from the server on boot.
*/
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** What is being protected, which decides what the fallback says and how much it claims is lost. */
  scope: 'app' | 'panel';
  /** Named so the message can say which surface failed rather than "something". */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
      Logged, and deliberately still logged in production.

      This client has no error reporting service and adding one is a data-egress decision nobody has
      taken — the whole product exists to keep HR data inside a tenant. So the console is the only
      record, and the end-to-end suite asserts the console is clean over a whole run, which means a
      swallowed throw here would make that assertion pass while the app was broken.
    */
    console.error(`[${this.props.scope}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const what = this.props.label ? `The ${this.props.label}` : 'This part of the screen';

    if (this.props.scope === 'panel') {
      return (
        <div className="banner error" role="alert" data-testid="panel-error">
          <div>
            <strong>{what} could not be drawn.</strong> Everything else — the chart, your changes and
            everyone else on the project — is unaffected. Reload to bring it back.
          </div>
        </div>
      );
    }

    return (
      <div className="centred" data-testid="app-error">
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Something went wrong</h2>
          <p className="muted">
            The application stopped drawing. Nothing you had committed is lost — every change is on
            the server the moment it is made, and reloading restores this tab from it.
          </p>
          <p className="muted small">{error.message}</p>
          <button className="primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
